import { isDefined } from '@sgme/fp';
import { CheckBoxButtonsGroup } from 'components/common-form/builder/form/node/field/CheckboxField';
import type { BackToBackOwnProps } from 'components/common-form/sidePanel/actions/trade/backToBack/shared';
import { AmountWithUnitAndLabel } from 'components/shared/Amount/Amount';
import { FieldErrorWrapper } from 'components/shared/FormGroup/ErrorMessages/FieldErrorWrapper';
import { WithFormLabel } from 'components/shared/WithLabel/WithLabel';
import { connect } from 'react-redux';
import { getQuoteCurrency, getTradeConfirmation } from 'state/selectors';
import type { ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';
import {
  tradeConfirmationSetBackedNotionalUnit,
  tradeConfirmationSetBackToBackFieldValue,
} from 'state/uiPanelState/tradeConfirmation/tradeConfirm.action';
import type {
  TradeConfirmationBackToBack,
  TradeConfirmationBackToBackFields,
} from 'state/uiPanelState/tradeConfirmation/tradeConfirm.model';

type StateProps = Pick<TradeConfirmationBackToBack, 'backedNotional' | 'isDelayedBackToBack'> & { currency?: string };
type DispatchProps = {
  setBackedNotional: (backedNotional?: number) => void;
  setBackedNotionalUnit: (unit: string) => void;
  setIsDelayedBackToBack: (isDelayedBackToBack?: boolean) => void;
};
type ClientTradeWorkflowProps = StateProps & DispatchProps;

export function ClientTradeWorkflow({
  backedNotional,
  setBackedNotional,
  setBackedNotionalUnit,
  setIsDelayedBackToBack,
  currency,
  isDelayedBackToBack,
}: ClientTradeWorkflowProps) {
  return (
    <>
      {isDelayedBackToBack?.value === true ? null : (
        <FieldErrorWrapper<TradeConfirmationBackToBackFields> inputId="backedNotional">
          <AmountWithUnitAndLabel
            useFlex
            className="flex-wrap  my-1"
            value={backedNotional?.value}
            isReadOnly={backedNotional?.isReadonly}
            onChange={setBackedNotional}
            labelKey="form.BackedNotional"
            units={isDefined(currency) ? [currency, '%'] : undefined}
            unit={backedNotional?.unit}
            unitsDirection="down"
            onUnitSelected={setBackedNotionalUnit}
            id={'backedNotional'}
          />
        </FieldErrorWrapper>
      )}
      <FieldErrorWrapper<TradeConfirmationBackToBackFields> inputId="isDelayedBackToBack">
        <WithFormLabel className="flex-wrap  my-1" useFlex id="isDelayedBackToBack" labelKey="form.IsDelayedBackToBack">
          <CheckBoxButtonsGroup
            id="isDelayedBackToBack"
            isNullable={true}
            className="w-100 "
            isDisabled={isDelayedBackToBack?.isReadonly}
            value={isDelayedBackToBack?.value ?? null}
            translationPrefix="form"
            onChange={value => setIsDelayedBackToBack(value ?? undefined)}
          />
        </WithFormLabel>
      </FieldErrorWrapper>
    </>
  );
}

const mapDispatchToProps = (dispatch: ThunkDispatch, { quoteId }: BackToBackOwnProps): DispatchProps => ({
  setBackedNotional(backedNotional) {
    dispatch(tradeConfirmationSetBackToBackFieldValue(quoteId, 'backedNotional', backedNotional));
  },
  setBackedNotionalUnit(unit) {
    dispatch(tradeConfirmationSetBackedNotionalUnit(quoteId, unit));
  },
  setIsDelayedBackToBack(isDelayedBackToBack) {
    dispatch(tradeConfirmationSetBackToBackFieldValue(quoteId, 'isDelayedBackToBack', isDelayedBackToBack));
  },
});
const mapStateToProps = (state: AppState, { formId, workspace }: BackToBackOwnProps): StateProps => {
  const tradeConfirmation = getTradeConfirmation(state, workspace, formId);
  const currency = getQuoteCurrency(state, workspace, formId);
  return {
    backedNotional: tradeConfirmation.backedNotional,
    isDelayedBackToBack: tradeConfirmation.isDelayedBackToBack,
    currency,
  };
};
export const ConnectedClientTradeWorkflow = connect(mapStateToProps, mapDispatchToProps)(ClientTradeWorkflow);
